import { useEffect, useMemo, useState } from 'react';

import { formatDayLabel } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_components/reschedule-slots-modal/_consts/format-day-label';
import { getAvailableDates } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/get-appointment-avaiable-dates.service';
import {
  getAvailableHours,
  type RescheduleAvailableHour,
} from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/get-appointment-avaiable-hours.service';
import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

const VISIBLE_DAYS = 3;
const SLOTS_PAGE_SIZE = 10;

const hoursCacheKey = (appointmentId: string, date: string) => `${appointmentId}|${date}`;

export interface AvailableDay {
  date: string;
  weekday: string;
  dayMonth: string;
  slots: RescheduleAvailableHour[];
  totalSlots: number;
}

export interface UseRescheduleSlotsResult {
  isLoading: boolean;
  isLoadingHours: boolean;
  availableDays: AvailableDay[];
  hasPrevious: boolean;
  hasNext: boolean;
  hasMoreSlots: boolean;
  isEmpty: boolean;
  hasScheduled: boolean;
  hasArrivalOrder: boolean;
  goToPreviousDays: () => void;
  goToNextDays: () => void;
  loadMoreSlots: () => void;
}

export const useRescheduleSlots = (
  appointmentId: string,
  visibleDays: number = VISIBLE_DAYS,
  scheduleTypeFilter?: ScheduleType,
): UseRescheduleSlotsResult => {
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [isLoadingDates, setIsLoadingDates] = useState<boolean>(true);
  const [hoursByDate, setHoursByDate] = useState<Record<string, RescheduleAvailableHour[]>>({});
  const [dayWindowOffset, setDayWindowOffset] = useState<number>(0);
  const [slotsLimitPerDay, setSlotsLimitPerDay] = useState<number>(SLOTS_PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingDates(true);
    setDayWindowOffset(0);
    setSlotsLimitPerDay(SLOTS_PAGE_SIZE);
    getAvailableDates({ id: appointmentId })
      .then((dates) => {
        if (cancelled) {
          return;
        }
        setAvailableDates(dates.map((d) => d.date));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setAvailableDates([]);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }
        setIsLoadingDates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  useEffect(() => {
    setDayWindowOffset(0);
    setSlotsLimitPerDay(SLOTS_PAGE_SIZE);
  }, [scheduleTypeFilter, visibleDays]);

  const visibleDates = useMemo(() => {
    const maxDatesToCheck = availableDates.length - dayWindowOffset;
    const lookAhead = Math.min(maxDatesToCheck, visibleDays * 5);
    return availableDates.slice(dayWindowOffset, dayWindowOffset + lookAhead);
  }, [availableDates, dayWindowOffset, visibleDays]);

  useEffect(() => {
    const toFetch = visibleDates.filter((date) => !(hoursCacheKey(appointmentId, date) in hoursByDate));
    if (toFetch.length === 0) {
      return;
    }
    let cancelled = false;

    Promise.all(
      toFetch.map((date) =>
        getAvailableHours({ id: appointmentId, date })
          .then((response) => ({
            date,
            hours: [
              ...response[ScheduleType.SCHEDULED],
              ...response[ScheduleType.ARRIVAL_ORDER],
              ...response[ScheduleType.LABORATORIAL],
            ],
          }))
          .catch(() => ({ date, hours: [] as RescheduleAvailableHour[] })),
      ),
    ).then((results) => {
      if (cancelled) {
        return;
      }
      setHoursByDate((prev) => {
        const next = { ...prev };
        for (const { date, hours } of results) {
          next[hoursCacheKey(appointmentId, date)] = hours;
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, visibleDates, hoursByDate]);

  const availableDays = useMemo<AvailableDay[]>(() => {
    const days: AvailableDay[] = [];
    for (const date of visibleDates) {
      if (days.length >= visibleDays) {
        break;
      }
      const hours = hoursByDate[hoursCacheKey(appointmentId, date)];
      if (!hours || hours.length === 0) {
        continue;
      }
      const filtered = scheduleTypeFilter
        ? hours.filter((h) =>
            scheduleTypeFilter === ScheduleType.ARRIVAL_ORDER
              ? h.scheduleType === ScheduleType.ARRIVAL_ORDER || h.scheduleType === ScheduleType.LABORATORIAL
              : h.scheduleType === scheduleTypeFilter,
          )
        : hours;
      if (filtered.length === 0) {
        continue;
      }
      const sorted = [...filtered].sort((a, b) => a.start.localeCompare(b.start));
      const label = formatDayLabel(`${date}T12:00:00-03:00`);
      days.push({
        date,
        weekday: label.weekday,
        dayMonth: label.dayMonth,
        slots: sorted.slice(0, slotsLimitPerDay),
        totalSlots: sorted.length,
      });
    }
    return days;
  }, [appointmentId, visibleDates, hoursByDate, slotsLimitPerDay, scheduleTypeFilter, visibleDays]);

  const hasMoreSlots = availableDays.some((d) => d.totalSlots > d.slots.length);

  const goToNextDays = () => {
    setDayWindowOffset((current) => {
      const next = current + visibleDays;
      return next >= availableDates.length ? current : next;
    });
  };

  const goToPreviousDays = () => {
    setDayWindowOffset((current) => Math.max(0, current - visibleDays));
  };

  const loadMoreSlots = () => setSlotsLimitPerDay((n) => n + SLOTS_PAGE_SIZE);

  const isLoadingHours = !isLoadingDates
    && visibleDates.length > 0
    && visibleDates.some((date) => !(hoursCacheKey(appointmentId, date) in hoursByDate));

  const { hasScheduled, hasArrivalOrder } = useMemo(() => {
    const allHours = Object.values(hoursByDate).flat();
    return {
      hasScheduled: allHours.some((h) => h.scheduleType === ScheduleType.SCHEDULED),
      hasArrivalOrder: allHours.some(
        (h) => h.scheduleType === ScheduleType.ARRIVAL_ORDER || h.scheduleType === ScheduleType.LABORATORIAL,
      ),
    };
  }, [hoursByDate]);

  return {
    isLoading: isLoadingDates,
    isLoadingHours,
    availableDays,
    hasPrevious: dayWindowOffset > 0,
    hasNext: dayWindowOffset + visibleDays < availableDates.length,
    hasMoreSlots,
    isEmpty: !isLoadingDates && availableDates.length === 0,
    hasScheduled,
    hasArrivalOrder,
    goToPreviousDays,
    goToNextDays,
    loadMoreSlots,
  };
};
